import { useRef, useState, useCallback, useEffect } from "react";
import { useAtom } from "jotai";

import { UseVideoPlayerReturn } from "./types";

import { clamp } from "@/utils/MathHelper";
import { videoPlayerAtom } from "@/constants/atoms";

/**
 * Custom hook for managing video player state
 *
 * @returns Object containing videoRef, isPlaying state, and event handlers
 *
 * @example
 * ```tsx
 * const { videoRef, isPlaying, currentTime, duration, handlePlay, handlePause, handleEnded, handlePlayPause, handleSkip, handleSeek } = useVideoPlayer();
 * return <Video ref={videoRef} onPlay={handlePlay} onPause={handlePause} onEnded={handleEnded} />;
 * // Skip 10 seconds forward: handleSkip(10)
 * // Skip 10 seconds backward: handleSkip(-10)
 * ```
 */
const useVideoPlayer = (): UseVideoPlayerReturn => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoPlayerState, setVideoPlayerState] = useAtom(videoPlayerAtom);

  const handlePlay = useCallback(() => setIsPlaying(true), []);
  const handlePause = useCallback(() => setIsPlaying(false), []);
  const handleEnded = useCallback(() => setIsPlaying(false), []);

  const handlePlayPause = useCallback(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  }, [isPlaying]);

  /**
   * Unified skip handler - accepts positive (forward) or negative (backward) seconds
   * @param seconds - Number of seconds to skip (positive = forward, negative = backward)
   */
  const handleSkip = useCallback((seconds: number) => {
    if (videoRef.current) {
      const newTime = videoRef.current.currentTime + seconds;
      videoRef.current.currentTime = clamp(
        newTime,
        0,
        videoRef.current.duration,
      );
    }
  }, []);

  const handleSkipBackward = useCallback(
    (skipSeconds: number) => handleSkip(-skipSeconds),
    [handleSkip],
  );

  const handleSkipForward = useCallback(
    (skipSeconds: number) => handleSkip(skipSeconds),
    [handleSkip],
  );

  const handleSeek = useCallback((time: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = clamp(
        time,
        0,
        videoRef.current.duration || 0,
      );
    }
  }, []);

  /**
   * Track currentTime and duration from video element
   */
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const updateTime = () => {
      // Only update if not seeking or if currentTime is close to lastSeekTarget
      setVideoPlayerState((prev) => {
        const timeDiff =
          prev.lastSeekTarget !== null
            ? Math.abs(video.currentTime - prev.lastSeekTarget)
            : 0;

        // If we have a seek target and currentTime is far from it, it's a stale event
        if (prev.lastSeekTarget !== null && timeDiff > 2) {
          return prev; // Ignore stale update
        }

        // If playback has progressed past seek point, clear the seek target
        if (
          prev.lastSeekTarget !== null &&
          video.currentTime > prev.lastSeekTarget + 0.3
        ) {
          return {
            ...prev,
            currentTime: video.currentTime,
            lastSeekTarget: null,
          };
        }

        return {
          ...prev,
          currentTime: video.currentTime,
        };
      });
    };

    const updateDuration = () => {
      setVideoPlayerState((prev) => ({
        ...prev,
        duration: video.duration,
      }));
    };

    video.addEventListener("timeupdate", updateTime);
    video.addEventListener("loadedmetadata", updateDuration);
    video.addEventListener("durationchange", updateDuration);

    // Initialize values if already loaded
    if (video.duration) {
      setVideoPlayerState((prev) => ({
        ...prev,
        duration: video.duration,
      }));
    }
    if (video.currentTime) {
      setVideoPlayerState((prev) => ({
        ...prev,
        currentTime: video.currentTime,
      }));
    }

    return () => {
      video.removeEventListener("timeupdate", updateTime);
      video.removeEventListener("loadedmetadata", updateDuration);
      video.removeEventListener("durationchange", updateDuration);
    };
  }, [setVideoPlayerState]);

  return {
    videoRef,
    isPlaying,
    currentTime: videoPlayerState.currentTime,
    duration: videoPlayerState.duration,
    handlePlay,
    handlePause,
    handleEnded,
    handlePlayPause,
    handleSkip,
    handleSkipBackward,
    handleSkipForward,
    handleSeek,
  };
};

export { useVideoPlayer };
